import React, { useEffect, useMemo, useRef, useState } from 'react';
import RecruitmentFilterBar from '../AnalyticsRecruitment/components/RecruitmentFilterBar';
import WidgetShell from '../AnalyticsRecruitment/components/WidgetShell';
import WidgetLibraryModal from '../AnalyticsRecruitment/components/WidgetLibraryModal';
import teamAnalyticsLibrary from './analyticsLibrary';
import RecruiterActivityModal from './components/RecruiterActivityModal';
import { getAnalyticsScreenDefaults, ANALYTICS_SCREEN_IDS } from '../../analyticsScreenDefaults';
import { getAnalyticsToolbarActions } from '../../analyticsToolbar';
import {
  canUseMaterializedAnalyticsBundle,
  buildScreenFilterModels,
  getSupportedAnalyticsFilterState,
  getLocalizedGroupByOptions,
  normalizeAnalyticsScreenFilterOptions,
  useSessionAnalyticsFilters
} from '../../analyticsFilters';
import {
  createUnavailableAnalyticsBundle,
  mergeAnalyticsWidgetNode,
  useAnalyticsScreenFilterOptions,
  useAnalyticsWidgetData,
  useQueriedAnalyticsWidgetData,
  useSharedAnalyticsDashboard
} from '../../analyticsRtdb';
import { createVisualModel, ANALYTICS_DATA_STATUS } from '../../visuals/visualDataModel';
import { ANALYTICS_GRAPH_TYPES } from '../../visuals/graphCatalog';
import { getCompatibleGraphs, resolveVisualRenderer } from '../../visuals/visualRegistry';
import UnavailableVisual from '../../visuals/UnavailableVisual';
import { atsAnalyticsApi } from '../../../../services/atsAnalyticsService';
import useCanonicalAnalyticsExport from '../../useCanonicalAnalyticsExport';

const ADJACENT_WIDGETS = {
  recruiter_radar: 'load_vs_outcome',
  load_vs_outcome: 'recruiter_radar',
  time_to_fill: 'pipeline_conversion',
  pipeline_conversion: 'time_to_fill',
  recruiter_velocity: 'offer_performance',
  offer_performance: 'recruiter_velocity'
};
const SCREEN_DEFAULTS = getAnalyticsScreenDefaults(ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE);
const BACKEND_WIDGET_IDS = new Set([
  'recruiter_scorecard',
  'recruiter_radar',
  'load_vs_outcome',
  'time_to_fill',
  'pipeline_conversion',
  'recruiter_velocity',
  'offer_performance',
  'retention_by_recruiter'
]);

const COPY = {
  en: {
    library: {
      title: 'Widget Library',
      subtitle: 'Add recruiter performance widgets below. Live recruiter analytics populate automatically as data becomes available.',
      close: 'Close',
      add: 'Add widget',
      added: 'Added',
      exampleDataReady: 'Data available',
      exampleDataMissing: 'Not available yet',
      graphCount: (count) => `${count} graph${count === 1 ? '' : 's'}`,
      categories: {
        recruiter_scorecards: 'Recruiter Scorecards',
        recruiter_speed: 'Time & Velocity',
        recruiter_pipeline: 'Pipeline & Offer Quality'
      }
    },
    menu: {
      settings: 'Widget settings',
      chartType: 'Chart type',
      groupBy: 'Group by',
      cardVariant: 'Card variant',
      cardSize: 'Card size',
      resize: 'Resize widget',
      compact: 'Compact',
      medium: 'Medium',
      'three-quarter': 'Three quarter',
      large: 'Large',
      'data-forward': 'Data forward',
      contextual: 'Contextual',
      executive: 'Executive',
      unavailable: 'No compatible data is available for this graph yet.',
      graphs: {
        [ANALYTICS_GRAPH_TYPES.RECRUITER_CARDS]: 'Recruiter cards',
        [ANALYTICS_GRAPH_TYPES.CONDITIONAL_TABLE]: 'Heatmap',
        [ANALYTICS_GRAPH_TYPES.RADAR]: 'Radar chart',
        [ANALYTICS_GRAPH_TYPES.SCATTER]: 'Scatter plot',
        [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART]: 'Bubble chart',
        [ANALYTICS_GRAPH_TYPES.TABLE]: 'Table',
        [ANALYTICS_GRAPH_TYPES.BOX_PLOT]: 'Box plot',
        [ANALYTICS_GRAPH_TYPES.LINE_OVER_TIME]: 'Line over time',
        [ANALYTICS_GRAPH_TYPES.HEATMAP]: 'Heatmap'
      }
    },
    scorecardHelp: 'Composite score (0-100): 25% offer acceptance, 20% pipeline conversion, 20% onboarding completion, 20% time-to-fill speed, and 15% low candidate drop-off. Time-to-fill speed starts at 100 and subtracts 3 points per average fill day, down to 0.'
  },
  ar: {
    library: {
      title: 'مكتبة العناصر',
      subtitle: 'أضف عناصر أداء مسؤولي التوظيف أدناه. يتم تحديثها من بيانات التوظيف والعروض والموظفين الفعلية عند توفرها.',
      close: 'إغلاق',
      add: 'إضافة عنصر',
      added: 'مضاف',
      exampleDataReady: 'البيانات التجريبية جاهزة',
      exampleDataMissing: 'البيانات غير متاحة',
      graphCount: (count) => `${count} رسم`,
      categories: {
        recruiter_scorecards: 'بطاقات أداء مسؤولي التوظيف',
        recruiter_speed: 'الوقت والسرعة',
        recruiter_pipeline: 'المسار وجودة العروض'
      }
    },
    menu: {
      settings: 'إعدادات العنصر',
      chartType: 'نوع الرسم',
      groupBy: 'التجميع حسب',
      cardVariant: 'نمط البطاقة',
      cardSize: 'حجم البطاقة',
      resize: 'تغيير حجم العنصر',
      compact: 'مصغر',
      medium: 'متوسط',
      'three-quarter': 'ثلاثة أرباع',
      large: 'كبير',
      'data-forward': 'يركز على البيانات',
      contextual: 'تشغيلي',
      executive: 'تنفيذي',
      unavailable: 'لا توجد بيانات متوافقة لهذا الرسم حتى الآن.',
      graphs: {
        [ANALYTICS_GRAPH_TYPES.RECRUITER_CARDS]: 'بطاقات المسؤولين',
        [ANALYTICS_GRAPH_TYPES.CONDITIONAL_TABLE]: 'خريطة حرارية',
        [ANALYTICS_GRAPH_TYPES.RADAR]: 'مخطط راداري',
        [ANALYTICS_GRAPH_TYPES.SCATTER]: 'مخطط مبعثر',
        [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART]: 'مخطط فقاعي',
        [ANALYTICS_GRAPH_TYPES.TABLE]: 'جدول',
        [ANALYTICS_GRAPH_TYPES.BOX_PLOT]: 'مخطط صندوقي',
        [ANALYTICS_GRAPH_TYPES.LINE_OVER_TIME]: 'خط زمني',
        [ANALYTICS_GRAPH_TYPES.HEATMAP]: 'خريطة حرارية'
      }
    },
    scorecardHelp: 'الدرجة المركبة (0-100): 25% قبول العروض، 20% تحويل المسار إلى تعيينات، 20% إكمال الإعداد، 20% سرعة ملء الشواغر، و15% انخفاض انسحاب المرشحين. تبدأ سرعة الملء من 100 وتخصم 3 نقاط لكل يوم متوسط حتى 0.'
  }
};

const AnalyticsTeam = ({ isRTL = false, companyUID = '', analyticsActorId = 'system' }) => {
  const copy = isRTL ? COPY.ar : COPY.en;
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [focusedRecruiterId, setFocusedRecruiterId] = useState('');
  const [activityRecruiterId, setActivityRecruiterId] = useState(null);
  const { filterState, setFilterValue } = useSessionAnalyticsFilters({ screenId: SCREEN_DEFAULTS.screenId });
  const {
    visibleWidgetIds,
    widgetPrefs,
    mutateDashboardState,
    resetDashboardState,
    dashboardReady
  } = useSharedAnalyticsDashboard({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    defaultVisibleWidgetIds: SCREEN_DEFAULTS.defaultVisibleWidgetIds,
    defaultWidgetPrefs: SCREEN_DEFAULTS.defaultWidgetPrefs,
    locked: SCREEN_DEFAULTS.locked,
      updatedBy: analyticsActorId
  });
  const { screenFilterNode: remoteFilterNode, loading: remoteFilterLoading } = useAnalyticsScreenFilterOptions({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId
  });
  const { widgetNodes, loading: widgetLoading } = useAnalyticsWidgetData({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    widgetIds: dashboardReady ? visibleWidgetIds : []
  });
  const fallbackFilterOptions = useMemo(() => ({}), []);
  const filterOptions = useMemo(
    () =>
      normalizeAnalyticsScreenFilterOptions({
        screenId: SCREEN_DEFAULTS.screenId,
        isRTL,
        remoteOptions: remoteFilterNode?.options || {},
        fallbackOptions: fallbackFilterOptions
      }),
    [fallbackFilterOptions, isRTL, remoteFilterNode]
  );
  const filterModels = useMemo(
    () =>
      buildScreenFilterModels({
        screenId: SCREEN_DEFAULTS.screenId,
        isRTL,
        filterState,
        filterOptions
      }),
    [filterOptions, filterState, isRTL]
  );
  const localizedGroupByOptions = useMemo(() => getLocalizedGroupByOptions(isRTL), [isRTL]);
  const selectedRecruiterId = widgetPrefs.recruiter_radar?.selectorState?.recruiterId || focusedRecruiterId;

  const widgetEntries = useMemo(
    () => visibleWidgetIds.map((id) => teamAnalyticsLibrary.widgets.find((widget) => widget.id === id)).filter(Boolean),
    [visibleWidgetIds]
  );

  const widgetRuntimeConfig = useMemo(
    () =>
      Object.fromEntries(
        widgetEntries.map((entry) => {
          const prefs = widgetPrefs[entry.id] || {};
          const activeGroupBy = prefs.selectorState?.groupBy || entry.defaultGroupBy || '';
          const effectiveFilters = getSupportedAnalyticsFilterState({
            filterState,
            supportedFilters: entry.supportedFilters || [],
            groupBy: activeGroupBy
          });
          const selectedRecruiterIds = Array.isArray(effectiveFilters.recruiters)
            ? effectiveFilters.recruiters
            : effectiveFilters.recruiters && effectiveFilters.recruiters !== 'all'
              ? [effectiveFilters.recruiters]
              : [];
          const effectiveRecruiterId =
            selectedRecruiterIds.length
              ? (selectedRecruiterIds.includes(selectedRecruiterId) ? selectedRecruiterId : selectedRecruiterIds[0])
              : selectedRecruiterId;
          const materializedGroupBy = Array.isArray(widgetNodes[entry.id]?.supportedGroupBy)
            ? widgetNodes[entry.id].supportedGroupBy
            : [];
          const canUseMaterializedBundle = canUseMaterializedAnalyticsBundle({
            screenId: SCREEN_DEFAULTS.screenId,
            filterState,
            supportedFilters: entry.supportedFilters || [],
            supportsGroupBy: entry.supportsGroupBy,
            activeGroupBy,
            defaultGroupBy: entry.defaultGroupBy || '',
            materializedGroupBy
          });

          return [entry.id, {
            prefs,
            activeGroupBy,
            effectiveFilters,
            effectiveRecruiterId,
            canUseMaterializedBundle
          }];
        })
      ),
    [filterState, selectedRecruiterId, widgetEntries, widgetNodes, widgetPrefs]
  );

  const { widgetNodes: queriedWidgetNodes, loading: queriedLoading } = useQueriedAnalyticsWidgetData({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    widgetQueryMap: Object.fromEntries(
      widgetEntries.map((entry) => {
        const runtime = widgetRuntimeConfig[entry.id] || {};
        const hasMaterializedNode = Boolean(widgetNodes[entry.id]);
        return [entry.id, {
          enabled: Boolean(companyUID) && (!runtime.canUseMaterializedBundle || !hasMaterializedNode),
          filters: runtime.effectiveFilters || {},
          groupBy: runtime.activeGroupBy || ''
        }];
      })
    )
  });
  const widgetLibraryCategories = useMemo(
    () =>
      teamAnalyticsLibrary.categories.map((category) => ({
        id: category.id,
        label: copy.library.categories[category.id] || category.label,
        widgets: category.widgets.map((widget) => ({
          id: widget.id,
          label: widget.label,
          defaultGraphLabel: copy.menu.graphs[widget.defaultGraph] || widget.defaultGraph,
          graphCount: widget.supportedGraphs.length,
          isAdded: visibleWidgetIds.includes(widget.id),
          hasReadyData: Boolean(widgetNodes[widget.id] || queriedWidgetNodes[widget.id])
        }))
      })),
    [copy.library.categories, copy.menu.graphs, queriedWidgetNodes, visibleWidgetIds, widgetNodes]
  );
  const isScreenLoading =
    Boolean(companyUID) &&
    (!dashboardReady ||
      remoteFilterLoading ||
      ((widgetLoading || queriedLoading) &&
        !Object.keys(widgetNodes).length &&
        !Object.keys(queriedWidgetNodes).length));

  const widgetModels = useMemo(
    () =>
      widgetEntries.map((entry) => {
        const runtime = widgetRuntimeConfig[entry.id] || {};
        const prefs = runtime.prefs || widgetPrefs[entry.id] || {};
        const activeGroupBy = runtime.activeGroupBy || entry.defaultGroupBy || '';
        const effectiveFilters = runtime.effectiveFilters || {};
        const effectiveRecruiterId = runtime.effectiveRecruiterId || selectedRecruiterId;
        const unavailableBundle = createUnavailableAnalyticsBundle({
          title: entry.label,
          annotationItems: [],
          supportedFilters: entry.supportedFilters || [],
          supportedGroupBy: entry.groupByOptions || []
        });
        const hasMaterializedNode = Boolean(widgetNodes[entry.id]);
        const canUseMaterializedBundle = Boolean(runtime.canUseMaterializedBundle) && hasMaterializedNode;
        const selectedViewId = entry.id === 'recruiter_radar' ? effectiveRecruiterId : '';
        const bundle = canUseMaterializedBundle
          ? mergeAnalyticsWidgetNode(widgetNodes[entry.id], unavailableBundle, { selectedViewId, isRTL })
          : queriedWidgetNodes[entry.id]
            ? mergeAnalyticsWidgetNode(queriedWidgetNodes[entry.id], unavailableBundle, { selectedViewId, isRTL })
            : companyUID && BACKEND_WIDGET_IDS.has(entry.id)
              ? unavailableBundle
              : unavailableBundle;
        const availableGraphIds = getCompatibleGraphs(entry, bundle, prefs.size || entry.defaultSize);
        const activeGraph = availableGraphIds.includes(prefs.activeGraph)
          ? prefs.activeGraph
          : availableGraphIds[0] || '';
        const dataset = activeGraph ? bundle.datasets[activeGraph] : null;

        return createVisualModel({
          id: entry.id,
          type: entry.visualType,
          size: prefs.size || entry.defaultSize,
          variant: prefs.variant || entry.defaultVariant,
          datasets: bundle.datasets,
          datasetShape: dataset?.shape || '',
          dataset,
          activeGraph,
          availableGraphs: availableGraphIds.map((graphId) => ({
            id: graphId,
            label: copy.menu.graphs[graphId] || graphId
          })),
          dataStatus:
            bundle.dataStatus === ANALYTICS_DATA_STATUS.READY && dataset
              ? ANALYTICS_DATA_STATUS.READY
              : bundle.dataStatus,
          title: bundle.title || entry.label,
          annotations: bundle.annotationItems || [],
          meta: {
            dataSemantics: entry.dataSemantics,
            graphConfig: entry.graphConfig,
            activeGroupBy,
            supportedGroupBy: bundle.supportedGroupBy || entry.groupByOptions || []
          },
          options: entry.id === 'recruiter_scorecard'
            ? {
                onSelectCategory: (recruiterId) => {
                  setFocusedRecruiterId(recruiterId);
                  mutateDashboardState((current) => ({
                    visibleWidgetIds: current.visibleWidgetIds.includes('recruiter_radar')
                      ? current.visibleWidgetIds
                      : [...current.visibleWidgetIds, 'recruiter_radar'],
                    widgetPrefs: {
                      ...current.widgetPrefs,
                      recruiter_radar: {
                        ...(current.widgetPrefs.recruiter_radar || {}),
                        selectorState: {
                          ...(current.widgetPrefs.recruiter_radar?.selectorState || {}),
                          recruiterId
                        }
                      }
                    }
                  }));
                  setActivityRecruiterId(recruiterId);
                }
              }
            : {}
        });
      }),
    [copy.menu.graphs, isRTL, mutateDashboardState, queriedWidgetNodes, selectedRecruiterId, widgetEntries, widgetNodes, widgetPrefs, widgetRuntimeConfig]
  );
  const {
    exporting,
    exportMode,
    handleExportAction
  } = useCanonicalAnalyticsExport({
    screenId: SCREEN_DEFAULTS.screenId,
    companyUID,
    analyticsActorId,
    filterState,
    widgetModels,
    language: isRTL ? 'ar' : 'en'
  });
  const toolbarActions = useMemo(
    () => getAnalyticsToolbarActions({
      isRTL,
      locked: SCREEN_DEFAULTS.locked,
      allowExport: true,
      exporting,
      exportMode
    }),
    [exportMode, exporting, isRTL]
  );

  const updateWidgetSize = (id, nextSize) => {
    mutateDashboardState((current) => {
      const nextWidgetPrefs = {
        ...current.widgetPrefs,
        [id]: {
          ...(current.widgetPrefs[id] || {}),
          size: nextSize
        }
      };

      const adjacentId = ADJACENT_WIDGETS[id];
      if (adjacentId && nextSize === 'three-quarter') {
        nextWidgetPrefs[adjacentId] = {
          ...(current.widgetPrefs[adjacentId] || {}),
          size: 'compact'
        };
      }

      if (adjacentId && current.widgetPrefs[adjacentId]?.size === 'three-quarter' && nextSize !== 'compact') {
        nextWidgetPrefs[adjacentId] = {
          ...(current.widgetPrefs[adjacentId] || {}),
          size: 'medium'
        };
      }

      return {
        ...current,
        widgetPrefs: nextWidgetPrefs
      };
    });
  };

  const [liveActivityDetail, setLiveActivityDetail] = useState(null);
  const activityFetchRef = useRef(null);

  useEffect(() => {
    if (!activityRecruiterId || !companyUID) {
      setLiveActivityDetail(null);
      return;
    }
    const controller = new AbortController();
    activityFetchRef.current = controller;
    atsAnalyticsApi
      .getRecruiterActivity(companyUID, activityRecruiterId, 30)
      .then((data) => {
        if (!controller.signal.aborted && data) setLiveActivityDetail(data);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [activityRecruiterId, companyUID]);

  const recruiterActivityDetail = activityRecruiterId
    ? (liveActivityDetail?.id === activityRecruiterId
        ? liveActivityDetail
        : null)
    : null;

  const updateChartType = (id, nextGraph) => {
    mutateDashboardState((current) => ({
      ...current,
      widgetPrefs: {
        ...current.widgetPrefs,
        [id]: {
          ...(current.widgetPrefs[id] || {}),
          activeGraph: nextGraph
        }
      }
    }));
  };

  const updateGroupBy = (id, nextGroupBy) => {
    mutateDashboardState((current) => ({
      ...current,
      widgetPrefs: {
        ...current.widgetPrefs,
        [id]: {
          ...(current.widgetPrefs[id] || {}),
          selectorState: {
            ...(current.widgetPrefs[id]?.selectorState || {}),
            groupBy: nextGroupBy
          }
        }
      }
    }));
  };

  const addWidget = (id) => {
    const entry = teamAnalyticsLibrary.widgets.find((widget) => widget.id === id);
    mutateDashboardState((current) => {
      if (current.visibleWidgetIds.includes(id)) return current;

      return {
        visibleWidgetIds: [...current.visibleWidgetIds, id],
        widgetPrefs: {
          ...current.widgetPrefs,
          [id]: current.widgetPrefs[id] || {
            size: entry?.defaultSize || 'medium',
            activeGraph: entry?.defaultGraph || ''
          }
        }
      };
    });
  };

  const handleToolbarAction = (actionId) => {
    if (handleExportAction(actionId)) {
      return;
    }

    if (actionId === 'add_widget') {
      setLibraryOpen(true);
      return;
    }

    if (actionId === 'reset_layout') {
      resetDashboardState();
    }
  };

  return (
    <section className={`analytics-recruitment${isRTL ? ' rtl' : ''}`}>
      {isScreenLoading ? (
        <div className="data-analytics-loading-screen">
          <i className="fa-solid fa-spinner fa-spin" />
          <p>{isRTL ? 'يرجى الانتظار' : 'Please Wait'}</p>
        </div>
      ) : (
        <>
      <RecruitmentFilterBar
        filters={filterModels}
        actions={toolbarActions}
        onAction={handleToolbarAction}
        onFilterChange={setFilterValue}
        isRTL={isRTL}
      />

      <div className="analytics-recruitment-canvas">
        {widgetModels.map((model) => {
          const Renderer = model.dataset ? resolveVisualRenderer(model.activeGraph, model.datasetShape) : null;
          const adjacentId = ADJACENT_WIDGETS[model.id];
          const pairedCompact = adjacentId ? widgetPrefs[adjacentId]?.size === 'three-quarter' : false;
          const groupByOptions = localizedGroupByOptions.filter((option) =>
            (model.meta.supportedGroupBy || []).includes(option.id)
          );

          return (
            <WidgetShell
              key={model.id}
              id={model.id}
              title={model.title}
              annotationItems={model.annotations}
              size={model.size}
              pairedCompact={pairedCompact}
              variant={model.variant}
              chartTypes={model.availableGraphs}
              activeChartType={model.activeGraph}
              groupByOptions={groupByOptions}
              activeGroupBy={model.meta.activeGroupBy}
              menuCopy={copy.menu}
              onChartTypeChange={updateChartType}
              onGroupByChange={updateGroupBy}
              onSizeChange={updateWidgetSize}
              allowResize={model.id !== 'recruiter_scorecard'}
              helpText={model.id === 'recruiter_scorecard' ? copy.scorecardHelp : ''}
              className={`analytics-recruitment-widget-${model.id}`}
              isRTL={isRTL}
            >
              {Renderer && model.dataStatus === ANALYTICS_DATA_STATUS.READY ? (
                <Renderer model={model} isRTL={isRTL} />
              ) : (
                <UnavailableVisual message={model.annotations[0] || copy.menu.unavailable} />
              )}
            </WidgetShell>
          );
        })}
      </div>

      <WidgetLibraryModal
        isOpen={libraryOpen}
        copy={copy.library}
        categories={widgetLibraryCategories}
        onClose={() => setLibraryOpen(false)}
        onAddWidget={addWidget}
        isRTL={isRTL}
      />

      <RecruiterActivityModal
        detail={recruiterActivityDetail}
        onClose={() => { setActivityRecruiterId(null); setLiveActivityDetail(null); }}
        isRTL={isRTL}
        closeLabel={isRTL ? 'إغلاق' : 'Close'}
      />
        </>
      )}
    </section>
  );
};

export default AnalyticsTeam;
